import { SelectObject } from '../SelectObject'

type BaseProps<T extends Record<string, any>> = {
  options: T[] | Readonly<T[]>
  label?: string
  'aria-label'?: string
  getOptionLabel?: (option: T) => string
  isOptionEqualToValue?: (option: T, value: T) => boolean
  error?: boolean
  helperText?: string | null
  required?: boolean
  disabled?: boolean
  fullWidth?: boolean
  size?: 'small' | 'medium'
  variant?: 'outlined' | 'standard' | 'filled'
}

interface FilterSelectObjectProps<
  T extends Record<string, any>,
  TFilters extends Record<string, any>,
  K extends keyof TFilters,
> extends BaseProps<T> {
  filterKey: K
  value: TFilters[K]
  onFilterChange: (filter: Partial<TFilters>, submit?: boolean) => void
  groupByProp?: keyof T
  groupBy?: (option: T) => string
  multiple?: boolean
}

export function FilterSelectObject<
  T extends Record<string, any>,
  TFilters extends Record<string, any>,
  K extends keyof TFilters,
>({
  filterKey,
  value,
  onFilterChange,
  groupByProp,
  groupBy,
  multiple,
  ...rest
}: FilterSelectObjectProps<T, TFilters, K>) {
  const handleChange = (newValue: T | T[] | null) => {
    onFilterChange({ [filterKey]: newValue } as Partial<TFilters>)
  }

  const resolvedGroupBy =
    groupBy ??
    (groupByProp ? (option: T) => String(option[groupByProp]) : undefined)

  if (multiple) {
    return (
      <SelectObject<T>
        {...rest}
        multiple
        value={(value as T[] | null) ?? []}
        onChange={handleChange as (value: T[]) => void}
        groupBy={resolvedGroupBy}
      />
    )
  }

  return (
    <SelectObject<T>
      {...rest}
      value={value as T | null}
      onChange={handleChange as (value: T | null) => void}
      groupBy={resolvedGroupBy}
    />
  )
}
